import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { X, Check, Plus, FolderHeart } from "lucide-react";

const BoardSelector = ({ item, onClose, onDone }) => {
  const { api } = useAuth();
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(null);
  const [newName, setNewName] = useState("");
  const [showNew, setShowNew] = useState(false);

  useEffect(() => {
    api.get("/boards").then((res) => setBoards(res.data)).finally(() => setLoading(false));
  }, [api]);

  const addToBoard = async (boardId) => {
    setAdding(boardId);
    try {
      await api.post(`/boards/${boardId}/content`, { content_id: item.id });
      setBoards((prev) => prev.map((b) =>
        b.id === boardId ? { ...b, content_ids: [...(b.content_ids || []), item.id] } : b
      ));
      onDone?.();
    } catch {} finally { setAdding(null); }
  };

  const createAndAdd = async () => {
    if (!newName.trim()) return;
    try {
      const res = await api.post("/boards", { name: newName.trim() });
      await api.post(`/boards/${res.data.id}/content`, { content_id: item.id });
      setBoards((prev) => [{ ...res.data, content_ids: [item.id] }, ...prev]);
      setNewName("");
      setShowNew(false);
      onDone?.();
    } catch {}
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px]" />
      <div
        className="relative bg-[#0c0c0e] border border-white/[0.08] rounded-2xl w-full max-w-sm shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: "fadeIn 0.2s ease-out" }}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
          <h3 className="text-white font-semibold text-sm">Add to Board</h3>
          <button onClick={onClose} className="text-[#555] hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="p-3 max-h-[300px] overflow-y-auto">
          {loading ? (
            <div className="py-8 text-center">
              <div className="w-5 h-5 border-2 border-white/10 border-t-white/50 rounded-full animate-spin mx-auto" />
            </div>
          ) : (
            <div className="space-y-1">
              {boards.map((board) => {
                const alreadyAdded = board.content_ids?.includes(item.id);
                return (
                  <button
                    key={board.id}
                    onClick={() => !alreadyAdded && addToBoard(board.id)}
                    disabled={alreadyAdded || adding === board.id}
                    className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-xl text-left transition-all duration-150 ${
                      alreadyAdded
                        ? "bg-emerald-500/[0.06] border border-emerald-500/[0.1]"
                        : "hover:bg-white/[0.03] border border-transparent"
                    }`}
                  >
                    <FolderHeart size={16} className={alreadyAdded ? "text-emerald-400/60" : "text-[#555]"} />
                    <span className={`text-[13px] flex-1 ${alreadyAdded ? "text-emerald-300/80" : "text-[#888]"}`}>{board.name}</span>
                    {alreadyAdded && <Check size={14} className="text-emerald-400/60" />}
                    {adding === board.id && <div className="w-3.5 h-3.5 border-2 border-white/10 border-t-white/50 rounded-full animate-spin" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-3 pb-3">
          {showNew ? (
            <div className="flex items-center gap-2">
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && createAndAdd()}
                placeholder="Board name..."
                className="flex-1 bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2.5 text-[13px] text-white placeholder:text-[#333] focus:outline-none focus:border-white/[0.15]"
                autoFocus
              />
              <button onClick={createAndAdd} className="text-[13px] text-white bg-white/[0.06] hover:bg-white/[0.1] rounded-lg px-3.5 py-2.5 transition-colors">Add</button>
            </div>
          ) : (
            <button
              onClick={() => setShowNew(true)}
              className="w-full flex items-center gap-2 px-3.5 py-3 rounded-xl text-[13px] text-[#555] hover:text-white/70 hover:bg-white/[0.03] transition-all"
            >
              <Plus size={15} />
              Create new board
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BoardSelector;
